import { SyntheticEvent, useState } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Stack, Tab, Typography as T } from "@mui/material";
import useDataset from "datasets/hooks/useDataset";
import AgentInstructionsEditor from "datasets/pages/settings/components/AgentInstructionsEditor";
import McpServerToggles from "datasets/pages/settings/components/McpServerToggles";
import SamplePromptsTab from "datasets/pages/settings/components/SamplePromptsTab";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import Switch from "~/components/Switch";
import useErrorToast from "~/contexts/error-toast";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const AiAgentsSettings = () => {
  const { data: dataset, mutate } = useDataset();
  const { showError } = useErrorToast();
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("instructions");

  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;
  const isEnabled = dataset?.aiAgentsEnabled === true;

  const handleToggle = async (checked: boolean) => {
    if (!dataset || submitting) return;

    setSubmitting(true);

    try {
      const resp = await fetch(`${VITE_CATALOG_API}/datasets/${dataset.id}/ai-agents-enabled`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: checked }),
      });

      if (!resp.ok) {
        throw Error("There was a problem updating AI agents settings");
      }

      trackEvent("dataset_ai_agents_toggled", {
        dataset_id: dataset.id,
        enabled: checked,
      });

      mutate((currentDataset) => {
        if (!currentDataset || currentDataset.id !== dataset.id) {
          return currentDataset;
        }
        return { ...currentDataset, aiAgentsEnabled: checked };
      });
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to update AI agents settings");
    }

    setSubmitting(false);
  };

  const handleTabChange = (_: SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  return (
    <Stack>
      <T component="h1" fontSize={20} fontWeight={600} lineHeight={1.8}>
        Analysis Agent
      </T>
      <Stack direction="row" alignItems="center" justifyContent="space-between" py={2}>
        <Box>
          <T fontSize={14} fontWeight={600}>
            Enable
          </T>
          <T variant="body2" color="text.secondary">
            Analysis agents are available to users with Owner and Analyze permissions only—not
            Preview.
          </T>
        </Box>
        <Switch
          checked={isEnabled}
          disabled={!isOwner || submitting}
          onChange={(e) => handleToggle(e.target.checked)}
        />
      </Stack>
      {isEnabled && (
        <TabContext value={activeTab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList
              onChange={handleTabChange}
              sx={{
                "& .MuiTabs-indicator": { backgroundColor: "text.primary" },
                "& .MuiTab-root.Mui-selected": { color: "text.primary" },
              }}
            >
              <Tab label="Instructions" value="instructions" />
              <Tab label="Prompts" value="prompts" />
              <Tab label="MCP Servers" value="mcp-servers" />
            </TabList>
          </Box>
          <TabPanel value="instructions" sx={{ px: 0, py: 3 }}>
            <AgentInstructionsEditor />
          </TabPanel>
          <TabPanel value="prompts" sx={{ px: 0, py: 3 }}>
            <SamplePromptsTab />
          </TabPanel>
          <TabPanel value="mcp-servers" sx={{ px: 0, py: 3 }}>
            <McpServerToggles />
          </TabPanel>
        </TabContext>
      )}
    </Stack>
  );
};

export default AiAgentsSettings;
